import React, { useRef, useState, Suspense } from 'react';
import { documents } from './Documents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEdit, faEye, faFileAlt, faFilePdf, faList } from '@fortawesome/free-solid-svg-icons';

function DocumentList() {
  const categoryRefs = useRef([]);
  const [activeDocIndex, setActiveDocIndex] = useState(null);
  const [activeComponent, setActiveComponent] = useState(null);
  const [loading, setLoading] = useState(false);

  const scrollToCategory = (index) => {
    categoryRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOpen = (doc) => {
    if (!doc.isActive) return;

    const fileName = `${doc.componentName}.${doc.docType}`;
    const fileUrl = `/startupkit/${fileName}`;

    window.open(fileUrl, "_blank");
  };

  const handleDownloadRaw = (doc) => {
    if (!doc.isActive) return;

    const fileName = `${doc.componentName}.${doc.docType}`;
    const fileUrl = `/startupkit/${fileName}`;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const handleDownloadPDF = (doc) => {
    if (!doc.isActive) return;

    const fileName = `${doc.componentName}.pdf`;
    const fileUrl = `/startupkit/${fileName}`;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.style.display = 'none';  
    document.body.appendChild(link);

    link.click();
    link.remove();
  };

  const handleEdit = async (doc, docIndex) => {
    try {
      setLoading(true);
      const ComponentModule = await import(`./components/${doc.componentName}`);
      setActiveComponent(() => ComponentModule.default);
      setActiveDocIndex(docIndex); 
    } catch (error) {
      console.error(`Error loading component: ${doc.componentName}`, error);
      setActiveComponent(() => () => <div className="text-red-500">Component not found</div>);
      setActiveDocIndex(docIndex);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="documents" className="py-4 px-4 bg-black">
      <div>
        <h1 className='text-white text-sm lg:text-m py-2'>Your Shortcut to MSEM & Startup Success</h1>
        <div className='flex flex-col items-center text-center'>
          <p className="text-xs lg:text-m font-medium text-gray-200">
            Save time, avoid costly mistakes, and scale your business with over 200 expert-crafted documents.
          </p>
        </div>
        <h2 className="text-sm lg:text-m font-bold text-center text-yellow-200 mb-4">
          Essential Documents for MSMEs & Startups
        </h2>
      </div>

      <div className="mx-auto mb-4 border-b border-blue-400 pb-2">
        <div
          className="
            flex flex-col gap-2
            lg:grid lg:grid-cols-3 lg:gap-1 lg:place-items-center
            w-full
          "
        >
          {documents.map((category, index) => (
            <div
              key={index}
              className="
                flex w-full lg:w-80 
                flex-row items-center space-x-2
              "
            >
              {/* Number Button */}
              <button
                className="
                  w-12 px-4 py-2
                  bg-blue-800 text-white text-xs lg:text-sm 
                  font-medium rounded-lg shadow
                  hover:bg-blue-600 transition
                  flex justify-center
                "
                onClick={() => scrollToCategory(index)}
              >
                {index + 1}
              </button>

              {/* Name Button */}
              <button
                className="
                  flex-1 px-4 py-2
                  bg-blue-800 text-white text-xs lg:text-sm 
                  font-medium rounded-lg shadow
                  hover:bg-blue-600 transition text-left
                "
                onClick={() => scrollToCategory(index)}
              >
                {category.name}
              </button>
            </div>
          ))}
        </div>
      </div>


      <div className="mx-auto">
      {documents.map((category, index) => (
        <div
          key={index}
          ref={(el) => (categoryRefs.current[index] = el)}
          className="mb-2"
        >
          <h3 className="text-m lg:text-lg font-semibold text-yellow-200 mb-4">
            {`${index + 1}. ${category.name}`}
          </h3>

          <ul className="space-y-4">
            {category.items.map((doc, idx) => {
              const docGlobalIndex = `${index}-${idx}`;
              const isActive = activeDocIndex === docGlobalIndex;

              return (
                <li
                  key={idx}
                  className="flex flex-col bg-black border border-gray-500 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex flex-row justify-between items-start w-full">
                    <div className="flex flex-row items-start">
                      <span className="text-yellow-500 font-bold mr-3">•</span>
                      <strong className="text-m lg:text-lg font-medium text-white">
                        {doc.name}
                      </strong>
                    </div>

                    <div className="flex flex-row gap-1 items-start">
                      <button
                        className="w-10 px-1 py-1 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition"
                        onClick={() => handleEdit(doc, docGlobalIndex)}
                        disabled={!doc.isActive}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>

                      <button
                        className="w-10 px-1 py-1 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition"
                        onClick={() => handleOpen(doc, docGlobalIndex)}
                        disabled={!doc.isActive}
                      >
                        <FontAwesomeIcon icon={faFileAlt} />
                      </button>

                      <button
                        className="w-10 px-1 py-1 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition"
                        onClick={() => handleDownloadRaw(doc, docGlobalIndex)}
                        disabled={!doc.isActive}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                      <button
                        className="w-10 px-1 py-1 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition"
                        onClick={() => handleDownloadPDF(doc, docGlobalIndex)}
                        disabled={!doc.isActive}
                      >
                        <FontAwesomeIcon icon={faFilePdf} />
                      </button>
                    </div>
                  </div>

                  <p className="mt-2 text-xs lg:text-sm text-blue-200">
                    {doc.usage}
                  </p>
                  <p className="mt-2 text-xs lg:text-sm text-blue-500">
                    Doc Type: {doc.docType}
                  </p>

                  {isActive && (
                    <div className="mt-4 bg-gray-700 p-4 rounded-lg">
                      {loading ? (
                        <p className="text-blue-500">Loading component...</p>
                      ) : (
                        <Suspense fallback={<p className="text-blue-500">Loading...</p>}>
                          {activeComponent &&
                            React.createElement(activeComponent, {
                              isFileReady: doc.isFileReady,
                              isComponentReady: doc.isComponentReady,
                            })}
                        </Suspense>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
    </section>
  );
}

export default DocumentList;
